import { SequentialTimeline } from '../../libs/SequentialTimeline';
import { Config } from '../../video-data/config';
import { assertMinimum, assertNumber } from '../../validators';
import { BaseGroup } from '../BaseGroup';
import { buildChildrenForSceneUsingData } from '../../libs/buildChildrenForScene';
import type { BaseAssetSettings } from '../asset/BaseAsset';
import type { LuminaryObject } from '../../types';
import { AssetSuffix, type BufferingMode, type SeekMode } from '../../enums';

/**
 */
export class Subscene extends BaseGroup implements LuminaryObject {
    override _classType = 'Subscene';
    override _name = 'subscene';

    /**
     * @internal
     */
    _timeline = new SequentialTimeline({
        name: 'subscene_timeline',
        duration: Config.MIN_SCENE_DURATION,
    });

    constructor(container: unknown, settings: BaseAssetSettings) {
        super(container, settings);

        this.on('assetCanPlay', () => {
            if (this.canPlay) {
                this._emitAssetsCanPlay();
            }
        });
    }

    get index(): number {
        if (!this.parent) {
            return -1;
        }
        return this.parent.children.indexOf(this);
    }

    /**
     * @internal
     */
    _emitAssetsCanPlay() {
        if (this.slide) {
            this.slide.emit('assetsCanPlay');
        }
    }

    /**
     * @internal
     */
    override _build() {
        this._buildTextInput();
    }

    /**
     * @internal
     */
    async _propagateBuffering(bufferingMode: BufferingMode): Promise<void> {
        await this._bufferAtTime(this._timeline.currentTime, bufferingMode);
    }

    /**
     * @internal
     */
    _buildTextInput() {
        this.getAssetsByType(Config.TEXT_ASSET_TYPE, { deep: true }).forEach((textAsset) => {
            textAsset.updateTextInput();
        });
    }

    /**
     * @internal
     */
    override _recursiveSeek(_progress: number, seekMode: SeekMode) {
        this._pixiContainer.visible = this._timeline.active;
        if (!this._timeline.active) {
            return;
        }
        super._recursiveSeek(this._timeline.progress, seekMode);
    }

    /**
     * Returns true if all assets (deeply) within this object have finished loading, or have
     * failed to load. If any assets are still loading or retrying a load this will be false.
     */
    get areAssetsDoneTryingToLoad(): boolean {
        return this.children.every((asset) => asset.isDoneTryingToLoad);
    }

    /**
     * Returns true if all assets (deeply) within this object have successfully loaded.
     */
    override get isLoaded(): boolean {
        return this.children.every((asset) => asset.isLoaded);
    }

    /**
     * Determines whether this component is active at the currentTime. Active means that the component will be
     * evaluated and visible when rendering.
     */
    get active(): boolean {
        return this._timeline.active;
    }

    get currentTime(): number {
        return this._timeline.currentTime;
    }

    /**
     * Get the subscene's end time relative to luminary.
     * @returns the end time of the subscene
     */
    get endTime(): number {
        return this.startTime + this.duration;
    }

    /**
     * Returns the subscene's start time relative to luminary.
     * @returns the start time of the subscene
     */
    get startTime(): number {
        return this._timeline.startTimeRelativeToAncestor(this._globalTimeline);
    }

    /**
     * The start time relative to the scene containing this subscene.
     */
    get relativeStartTime(): number {
        return this._timeline._relativeStartTime;
    }

    get animationManagers() {
        return this.getAssets().map((child) => child.animationManager);
    }

    /**
     * Determine whether or not the component is animated
     */
    get isAnimated(): boolean {
        // since we currently always create both text/image elements, but don't necessarily set them both, this value
        // can return a false negative. With the new update flow we'll be able to change this behavior is currently a
        // result of our rigid video data schema.
        return this.animationManagers.every((manager) => !manager.isEmpty());
    }

    /**
     * @internal
     */
    override set duration(value: number) {
        if (value === this.duration) {
            return;
        }
        assertNumber(value);
        assertMinimum(value, Config.MIN_SCENE_DURATION);
        this._timeline.duration = value;
        this.requiresRedraw = true;
    }

    override get duration() {
        return this._timeline.duration;
    }

    override get isPlaying() {
        return this._timeline.playing() && this._timeline.active;
    }

    /**
     * Generates new ids for all assets on the subscene.
     */
    generateNewAssetIds() {
        this.getAssets().forEach((child) => {
            child.generateNewId();
        });
        this.generateNewId();
    }

    override saveEditable() {
        return [
            {
                children: this.getAssets()
                    .filter((item) => item.isEditable)
                    .filter((item) => !item.hasSuffix(AssetSuffix.DisableSwap))
                    .map((item) => item.saveEditable()),
                duration: this.duration,
            },
        ];
    }

    override save(allProperties = true, clone = false) {
        const properties = super.save(allProperties, clone);
        return Object.assign(properties, this.saveChildren(allProperties, clone));
    }

    /**
     * Serializes all of the subscene's children data
     */
    saveChildren(allProperties = true, clone = false): { children: Array<object> } {
        return {
            children: this.getAssets()
                .map((item) => item.save(allProperties, clone))
                .flat(),
        };
    }

    override get readonlyProperties() {
        return ['classType', 'id'];
    }

    override get propertiesToSerialize(): string[] {
        return [];
    }

    override get editableProperties() {
        return super.editableProperties.concat(['duration']);
    }

    override load(data = {}) {
        this.clear();

        super.load(data);

        buildChildrenForSceneUsingData(this, data);

        return this;
    }

    get requiresUpdate() {
        return this.requiresRedraw;
    }

    set requiresUpdate(value: boolean) {
        // this is a no-op, but we need to implement it to satisfy the interface
    }
}
